import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import '../common/rtCommon.css';
import './AllocationReport.css';

const AllocationReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await apiGet('/reports/allocations/');
      if (response.status === 200 && response.data.success) {
        setReportData(response.data.data);
        toastSuccess('Report loaded successfully');
      } else {
        toastError('Failed to load report data');
      }
    } catch (error) {
      console.error('Error fetching allocation report:', error);
      toastError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchReportData();
  };

  const handleBack = () => {
    navigate('/reports');
  };

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <RTSpinner fullScreen={true} />
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div className="rt-title-row">
            <h2 className="rt-title">Allocation Report</h2>
            <div className="rt-actions">
              <RTButton variant="secondary" onClick={handleBack}>
                &#8592; Back
              </RTButton>
            </div>
          </div>
          <div className="no-data-message">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Allocation Report</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={handleBack}>
              &#8592; Back
            </RTButton>
            <RTButton variant="primary" onClick={handleRefresh}>
              <span className="refresh-icon">🔄</span> Refresh
            </RTButton>
          </div>
        </div>

        <div className="allocation-report-container">
          {/* Summary Metrics */}
          <div className="report-metrics">
            <div className="metric-card">
              <div className="metric-value">{reportData.total_active_allocations || 0}</div>
              <div className="metric-label">Total Active Allocations</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{reportData.active_allocations_with_discount_count || 0}</div>
              <div className="metric-label">Allocations with Discount</div>
            </div>
          </div>

          {/* Monthly Trends Charts */}
          <div className="charts-section">
            <div className="chart-container">
              <h3 className="chart-title">New Allocations - Last 6 Months</h3>
              <div className="bar-chart">
                {(() => {
                  const counts = reportData.new_allocations_monthly?.map(d => d.count) || [];
                  const maxCount = Math.max(...counts, 1);
                  const minCount = Math.min(...counts, 0);
                  
                  return reportData.new_allocations_monthly?.map((item, index) => {
                    let heightPercent;
                    if (maxCount === minCount) {
                      heightPercent = 80;
                    } else {
                      const normalized = (item.count - minCount) / (maxCount - minCount);
                      heightPercent = 20 + (normalized * 75);
                    }
                    
                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-value">{item.count}</div>
                        <div 
                          className="bar" 
                          style={{ height: `${heightPercent}%` }}
                          title={`${item.month}: ${item.count} allocations`}
                        ></div>
                        <div className="allocation-bar-label">{item.month}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="chart-container">
              <h3 className="chart-title">Ended Allocations - Last 6 Months</h3>
              <div className="bar-chart">
                {(() => {
                  const counts = reportData.ended_allocations_monthly?.map(d => d.count) || [];
                  const maxCount = Math.max(...counts, 1);
                  const minCount = Math.min(...counts, 0);
                  
                  return reportData.ended_allocations_monthly?.map((item, index) => {
                    let heightPercent;
                    if (maxCount === minCount) {
                      heightPercent = 80;
                    } else {
                      const normalized = (item.count - minCount) / (maxCount - minCount);
                      heightPercent = 20 + (normalized * 75);
                    }
                    
                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-value">{item.count}</div>
                        <div 
                          className="bar ended" 
                          style={{ height: `${heightPercent}%` }}
                          title={`${item.month}: ${item.count} allocations`}
                        ></div>
                        <div className="allocation-bar-label">{item.month}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Longest Active Allocations */}
          <div className="data-section">
            <h3 className="section-title">Top Longest Active Allocations</h3>
            <div className="data-grid">
              {reportData.longest_active_allocations?.map((allocation, index) => (
                <div key={index} className="data-card">
                  <div className="data-card-header">
                    <span className="rank">#{index + 1}</span>
                    <span className="duration-badge">
                      {allocation.duration_months} months
                    </span>
                  </div>
                  <div className="data-card-body">
                    <div className="data-row">
                      <strong>{allocation.pg_name}</strong>
                    </div>
                    <div className="data-row">
                      {allocation.building_name} • {allocation.room_type} • Bed #{allocation.bed_id}
                    </div>
                    <div className="data-row">
                      <span className="data-label">Start Date:</span>
                      <span className="data-value">{new Date(allocation.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Duration:</span>
                      <span className="data-value">{allocation.duration_days} days</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Highest Discount Allocations */}
          <div className="data-section">
            <h3 className="section-title">Top Active Allocations with Highest Discount</h3>
            <div className="data-grid">
              {reportData.highest_discount_allocations?.map((allocation, index) => (
                <div key={index} className="data-card discount-card">
                  <div className="data-card-header">
                    <span className="rank">#{index + 1}</span>
                    <span className="discount-badge">
                      {allocation.discount_type === 'percentage' 
                        ? `${allocation.discount_value}%` 
                        : `₹${allocation.discount_value}`}
                    </span>
                  </div>
                  <div className="data-card-body">
                    <div className="data-row">
                      <strong>{allocation.pg_name}</strong>
                    </div>
                    <div className="data-row">
                      {allocation.building_name} • {allocation.room_type} • Bed #{allocation.bed_id}
                    </div>
                    <div className="data-row">
                      <span className="data-label">Total Amount:</span>
                      <span className="data-value">₹{allocation.total_amount.toFixed(2)}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Payable Amount:</span>
                      <span className="data-value amount-highlight">₹{allocation.payable_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocationReport;
